import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react'
import { useI18n } from '../i18n'
import { useStore } from '../data/store'
import type { Booking } from '../data/model'
import { addDays, addMonths, eachDate, isWeekend, monthStart, nights, todayISO } from '../lib/dates'
import { dayShift, gripWidth, gripsFor, resizedRange, type BarEdge } from '../lib/calendarGrips'
import { Badge, Button, cx } from '../ui/kit'
import BookingSheet from './BookingSheet'
import { BookingBarContent, signalTitleLines, useBookingSignals } from './BookingBadges'
import BookingChangeDialog, { type PendingChange } from './BookingChangeDialog'
import NewBookingSheet, { type BookingPrefill } from './NewBookingSheet'
import PriceAdviceBar from './PriceAdviceBar'
import CalendarLegend, { LegendLink } from './CalendarLegend'
import {
  HolidayFootnote, HolidayHeaderHint, holidayTitle, useTenantHolidays,
} from './HolidayBands'

const DAY_W = 44          // px per day column on a phone
const DAY_W_LG = 56
/**
 * The calendar has no pages. It starts a month behind today and grows at whichever
 * end you scroll towards, so dragging left in December keeps working instead of
 * hitting a wall and making you reach for an arrow.
 */
const INITIAL_BACK = 31      // days rendered before the current month starts
const INITIAL_DAYS = 130     // roughly four months in the DOM to begin with
const GROW_BY = 60           // how much runway to add when an edge comes near
const EDGE_DAYS = 12         // how close to the edge triggers the growth

/**
 * Row height is the landlord's, not ours: six flats on a large screen want tall rows
 * with the guest name legible, twenty rooms want them compact enough to see the month
 * at a glance. Dragged on the divider in the name column, remembered in localStorage
 * so the next login opens the way the last one was left.
 */
const ROW_H_DEFAULT = 52
const ROW_H_MIN = 34
const ROW_H_MAX = 132
const ROW_H_KEY = 'staysuite.calendar.rowHeight'

function loadRowHeight(): number {
  try {
    const raw = Number(localStorage.getItem(ROW_H_KEY))
    if (!Number.isFinite(raw) || raw <= 0) return ROW_H_DEFAULT
    return Math.min(ROW_H_MAX, Math.max(ROW_H_MIN, Math.round(raw)))
  } catch { return ROW_H_DEFAULT }
}

export default function CalendarView() {
  const { t, formatDate, lang } = useI18n()
  const store = useStore()
  const scroller = useRef<HTMLDivElement>(null)
  /** Anzahlung, Rechnung, Haustier je Buchung — einmal indiziert, nicht je Balken. */
  const signalsOf = useBookingSignals()

  const [start, setStart] = useState(() => addDays(monthStart(todayISO()), -INITIAL_BACK))
  const [dayCount, setDayCount] = useState(INITIAL_DAYS)
  /** The month under the left edge — what the header should be naming. */
  const [visibleMonth, setVisibleMonth] = useState(() => monthStart(todayISO()))
  const [active, setActive] = useState<Booking | null>(null)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [drag, setDrag] = useState<{ unitId: string; from: string; to: string } | null>(null)
  const [change, setChange] = useState<PendingChange | null>(null)
  const [rowH, setRowH] = useState(loadRowHeight)
  /** The phone's + button asks for dates first, then offers what is free. */
  const [picker, setPicker] = useState(false)
  /**
   * Was eine eingefügte Anfrage über den Gast hergab. Reicht nur bis zum
   * Buchungsblatt und wird dort zum Anlegeformular — angelegt wird der Gast erst
   * auf Klick, damit ein abgebrochener Entwurf keine Karteileiche hinterlässt.
   */
  const [prefill, setPrefill] = useState<BookingPrefill | undefined>()

  /** Resizing the rows. The ref holds the gesture; state only drives the rendering. */
  const rowResize = useRef<
    { pointerId: number; startY: number; startH: number; current: number } | null>(null)

  /**
   * The gesture is tracked on `window`, not on the 8px divider: the pointer leaves
   * that strip on the very first move. Listeners rather than pointer capture, so a
   * drag that ends outside the window still commits.
   */
  const beginRowResize = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    const start = { pointerId: e.pointerId, startY: e.clientY, startH: rowH, current: rowH }
    rowResize.current = start

    const onMove = (ev: PointerEvent) => {
      const next = Math.min(ROW_H_MAX,
        Math.max(ROW_H_MIN, Math.round(start.startH + (ev.clientY - start.startY))))
      start.current = next
      setRowH(next)
    }
    const onUp = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
      document.body.style.userSelect = ''
      rowResize.current = null
      // Read off the closure, not off state: pointerup can land in the same React
      // batch as the last move, and state would then be one step behind.
      try { localStorage.setItem(ROW_H_KEY, String(start.current)) } catch { /* private mode */ }
    }
    document.body.style.userSelect = 'none'
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
  }

  /**
   * Dragging an existing booking.
   *
   * On touch this must not fight the calendar's own scrolling, so a drag only begins
   * after a long press; before that the gesture still belongs to the scroller. On a
   * mouse a few pixels of movement is enough. Nothing is written until the move is
   * confirmed — see BookingChangeDialog, because the price usually changes.
   *
   * Dieselben Handler tragen die dritte Geste: das Ziehen an einem der beiden
   * Balkenenden. Was läuft, entscheidet sich BEIM AUFSETZEN daran, ob unter dem
   * Finger ein Griff liegt (`edge`), und nicht aus der Bewegungsrichtung — sonst
   * hinge es an einer Heuristik, welche der drei Gesten der Vermieter gemeint hat.
   */
  const moveDrag_ = useRef<{
    booking: Booking
    pointerId: number
    startX: number
    startY: number
    originUnitIndex: number
    /** Welches Balkenende angefasst wurde. `null` heißt: der ganze Balken wandert. */
    edge: BarEdge | null
    armed: boolean
    timer: number | null
    el: HTMLElement | null
  } | null>(null)
  type Ghost = {
    unitId: string; from: string; to: string; bookingId: string
    /** Welches Datum sich bewegt — danach richtet sich die Beschriftung. */
    edge: BarEdge | null
  }
  // The ref is the source of truth and the state is only for rendering: pointerup can
  // land in the same React batch as the last pointermove, and a handler reading the
  // state would then still see the pre-drag value and silently drop the move.
  const ghostRef = useRef<Ghost | null>(null)
  const [ghost, setGhostState] = useState<Ghost | null>(null)
  const setGhost = (g: Ghost | null) => { ghostRef.current = g; setGhostState(g) }

  const days = useMemo(
    () => eachDate(start, addDays(start, dayCount)), [start, dayCount])

  const units = useMemo(
    () => store.db.units.filter((u) => u.active), [store.db.units])

  const rangeEnd = addDays(start, dayCount)
  const { byDay: holidays, coverage, regionCount } = useTenantHolidays(start, rangeEnd)
  const bookings = store.bookingsInRange(start, rangeEnd)

  // Kept in state rather than read inline, so the grid re-lays out when the
  // phone is rotated or a desktop window is resized.
  const [dayWidth, setDayWidth] = useState(
    () => (window.innerWidth >= 640 ? DAY_W_LG : DAY_W))

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 640px)')
    const apply = () => setDayWidth(mq.matches ? DAY_W_LG : DAY_W)
    apply()
    mq.addEventListener('change', apply)
    return () => mq.removeEventListener('change', apply)
  }, [])

  /**
   * Wie genau der Zeiger dieses Geräts zielt — entscheidet über die Griffbreite.
   *
   * Ein Hybridgerät (iPad mit Trackpad) meldet `coarse` und bekommt die großen
   * Griffe. Das ist die richtige Richtung des Irrtums: zu große Griffe kosten
   * Mitte, zu kleine kosten Treffer.
   */
  const [coarse, setCoarse] = useState(
    () => window.matchMedia('(pointer: coarse)').matches)

  useEffect(() => {
    const mq = window.matchMedia('(pointer: coarse)')
    const apply = () => setCoarse(mq.matches)
    apply()
    mq.addEventListener('change', apply)
    return () => mq.removeEventListener('change', apply)
  }, [])

  const offsetOf = useCallback(
    (date: string) => nights(start, date), [start])

  /**
   * Welche Buchungen abgerechnet und damit im Zeitraum festgeschrieben sind.
   *
   * Einmal je Kalenderdurchgang statt einmal je Balken — dieselbe Überlegung wie
   * beim Signalindex nebenan: der Kalender zeichnet über hundert Buchungen und baut
   * beim Scrollen ständig neu. Die Regel selbst bleibt dabei im Store; hier wird
   * nur gefragt, und zwar nur für Buchungen, zu denen es überhaupt eine Rechnung
   * gibt.
   */
  const { invoices } = store.db
  const invoiceLockFor = store.invoiceLockFor
  const lockedBookings = useMemo(() => {
    const gesperrt = new Set<string>()
    for (const id of new Set(invoices.map((i) => i.booking_id))) {
      if (id && invoiceLockFor(id).length > 0) gesperrt.add(id)
    }
    return gesperrt
  }, [invoices, invoiceLockFor])

  /**
   * Two years back and three forward. Long enough to reach any season a landlord
   * prices ahead for, short enough to stay a usable dropdown on a phone.
   */
  const monthChoices = useMemo(() => {
    const first = addMonths(monthStart(todayISO()), -24)
    return Array.from({ length: 61 }, (_, i) => addMonths(first, i))
  }, [])

  /** Occupancy over the next 30 days — the number a landlord actually watches. */
  const occupancy = useMemo(() => {
    const from = todayISO()
    const to = addDays(from, 30)
    const capacity = units.length * 30
    if (capacity === 0) return 0
    let sold = 0
    for (const b of store.bookingsInRange(from, to)) {
      if (b.kind === 'block' || b.status === 'cancelled') continue
      if (!['confirmed', 'checked_in', 'checked_out', 'option'].includes(b.status)) continue
      const s = b.arrival > from ? b.arrival : from
      const e = b.departure < to ? b.departure : to
      sold += Math.max(0, nights(s, e))
    }
    return Math.round((sold / capacity) * 100)
  }, [store, units.length])

  const dateFromClientX = (unitRow: HTMLElement, clientX: number): string => {
    const rect = unitRow.getBoundingClientRect()
    const idx = Math.floor((clientX - rect.left) / dayWidth)
    return addDays(start, Math.max(0, Math.min(dayCount - 1, idx)))
  }

  const beginDrag = (unitId: string) => (e: React.PointerEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('[data-booking]')) return
    const row = e.currentTarget
    row.setPointerCapture(e.pointerId)
    const d = dateFromClientX(row, e.clientX)
    setDrag({ unitId, from: d, to: addDays(d, 1) })
  }

  const moveDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!drag) return
    const d = dateFromClientX(e.currentTarget, e.clientX)
    const to = d >= drag.from ? addDays(d, 1) : addDays(drag.from, 1)
    setDrag({ ...drag, to })
  }

  const newBooking = (unitId: string, from: string, to: string) => {
    const draft: Booking = {
      id: crypto.randomUUID(),
      tenant_id: store.db.tenant.id,
      unit_id: unitId,
      guest_id: null,
      // Empty in live mode: a BEFORE INSERT trigger assigns the real reference from
      // number_series. Deriving it client-side drifts from the sequence and collides
      // with bookings created elsewhere — the public enquiry form hit exactly that.
      reference: store.live ? '' : store.nextReference('booking'),
      kind: 'booking',
      /*
       * Ein frisch aufgezogener Zeitraum ist eine OPTION, keine Bestätigung.
       *
       * Hier stand „bestätigt", damit der Balken überhaupt erscheint — gezeichnet und
       * gesperrt wird nur, was blockiert (BLOCKING_STATUSES), und eine Anfrage tut das
       * nicht: beim Ziehen wäre scheinbar nichts passiert. Der Reflex war richtig, das
       * Mittel falsch. Eine Buchung ohne Namen, ohne Anzahlung und ohne Zusage kann
       * nicht bestätigt sein, und wer sie so im Buchungsblatt vorfindet, glaubt dem
       * Kalender nicht mehr.
       *
       * Die Option blockiert die Einheit genauso — es kann niemand dazwischenbuchen —,
       * sagt aber die Wahrheit über den Stand und läuft über `option_until` von selbst
       * ab, statt Wochen später als feste Buchung dazustehen.
       */
      status: 'option',
      channel: 'direct',
      arrival: from,
      departure: to,
      adults: 2, children: 0, children_ages: [], optional_fee_ids: [],
      currency: store.db.tenant.currency,
      total_net: 0, total_vat: 0, total_gross: 0,
      prepayment_amount: 0, prepayment_due: null,
      deposit_amount: 0, deposit_status: 'not_required',
      internal_notes: '',
      created_at: new Date().toISOString(),
    }
    setDrag(null)
    // Ein im Kalender aufgezogener Zeitraum hat keine Anfrage hinter sich; ein
    // stehengebliebener Gastvorschlag von vorhin gehörte zu einer anderen Buchung.
    setPrefill(undefined)
    setActive(draft)
    setSheetOpen(true)
  }

  const endDrag = () => {
    if (!drag) return
    newBooking(drag.unitId, drag.from, drag.to)
  }

  const cancelMoveDrag = () => {
    const m = moveDrag_.current
    if (m?.timer) window.clearTimeout(m.timer)
    // Hand scrolling back to the calendar.
    if (m?.el) m.el.style.touchAction = ''
    moveDrag_.current = null
    setGhost(null)
  }

  const beginMove = (booking: Booking, unitIndex: number, frozen: boolean) =>
    (e: React.PointerEvent<HTMLButtonElement>) => {
      if (booking.status === 'cancelled') return
      const isTouch = e.pointerType === 'touch'
      e.currentTarget.setPointerCapture(e.pointerId)
      /*
       * Welche der drei Gesten gemeint ist, steht hier fest und wird später nicht
       * mehr nachverhandelt: liegt unter dem Finger ein Griff, wandert nur dieses
       * Ende; sonst der ganze Balken. (Dass gar kein Balken darunter liegt, hat
       * `beginDrag` eine Ebene höher schon abgefangen.)
       */
      const griff = (e.target as HTMLElement).closest('[data-griff]')
      const edge = griff?.getAttribute('data-griff') as BarEdge | undefined
      const entry = {
        booking,
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        originUnitIndex: unitIndex,
        edge: edge ?? null,
        /*
         * `frozen` ist die abgerechnete Buchung: sie armiert NIE. Das Antippen
         * bleibt erhalten — `endMove` öffnet für eine nicht armierte Geste das
         * Buchungsblatt —, aber gezogen wird gar nicht erst. Vorher begann die
         * Verschiebung und endete im Bestätigungsblatt mit einem gesperrten Knopf:
         * eine Geste anzubieten, die man hinterher zurücknehmen muss, ist die
         * schlechtere Art, nein zu sagen.
         */
        armed: !isTouch && !frozen,
        timer: null as number | null,
        el: e.currentTarget as HTMLElement,
      }
      if (isTouch && !frozen) {
        entry.timer = window.setTimeout(() => {
          if (moveDrag_.current === entry) {
            entry.armed = true
            // Only now take the gesture off the scroller, so a touch that starts on a
            // booking can still scroll the calendar until the long press completes.
            if (entry.el) entry.el.style.touchAction = 'none'
            if ('vibrate' in navigator) navigator.vibrate?.(8)
            setGhost({
              unitId: booking.unit_id, from: booking.arrival,
              to: booking.departure, bookingId: booking.id, edge: entry.edge,
            })
          }
        }, 320)
      }
      moveDrag_.current = entry
    }

  const moveMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const m = moveDrag_.current
    if (!m || m.pointerId !== e.pointerId) return

    const dx = e.clientX - m.startX
    const dy = e.clientY - m.startY

    if (!m.armed) {
      // Mouse arms immediately; touch waits for the long press. A touch that moves
      // before then is a scroll, so give the gesture back to the scroller.
      if (Math.hypot(dx, dy) > 10) cancelMoveDrag()
      return
    }

    const shift = dayShift(dx, dayWidth)

    /*
     * Am Griff wandert nur ein Datum — die Zeile bleibt, wo sie ist. Ein Griff, der
     * nebenbei die Einheit wechselt, wäre das Verschieben, und das gibt es schon.
     */
    if (m.edge) {
      const r = resizedRange(m.booking.arrival, m.booking.departure, m.edge, shift)
      setGhost({
        bookingId: m.booking.id,
        unitId: m.booking.unit_id,
        from: r.arrival,
        to: r.departure,
        edge: m.edge,
      })
      return
    }

    const rowShift = Math.round(dy / rowH)
    const targetIndex = Math.max(0, Math.min(units.length - 1, m.originUnitIndex + rowShift))
    const targetUnit = units[targetIndex]
    if (!targetUnit) return

    setGhost({
      bookingId: m.booking.id,
      unitId: targetUnit.id,
      from: addDays(m.booking.arrival, shift),
      to: addDays(m.booking.departure, shift),
      edge: null,
    })
  }

  const endMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const m = moveDrag_.current
    const g = ghostRef.current
    cancelMoveDrag()
    if (!m || m.pointerId !== e.pointerId) return

    /*
     * Not armed, or dropped where it started: treat as a plain tap.
     *
     * Die Abreise gehört seit den Griffen mit in den Vergleich: am hinteren Griff
     * bleiben Einheit UND Anreise gleich, und ohne diese dritte Bedingung hätte
     * jedes Verlängern als Antippen gegolten — der Vermieter zieht, und es öffnet
     * sich das Buchungsblatt. Für das Verschieben ändert die Zeile nichts, dort
     * wandern beide Daten ohnehin gemeinsam.
     */
    const unchanged = !g
      || (g.unitId === m.booking.unit_id
        && g.from === m.booking.arrival
        && g.to === m.booking.departure)
    if (!m.armed || unchanged) {
      setPrefill(undefined)
      setActive(m.booking)
      setSheetOpen(true)
      return
    }
    setChange({
      booking: m.booking, unitId: g.unitId, arrival: g.from, departure: g.to,
      kind: m.edge ? 'resize' : 'move',
    })
  }

  /** Scroll so that `date` sits a couple of columns in from the left edge. */
  const scrollToDate = useCallback((date: string, behavior: ScrollBehavior = 'smooth') => {
    requestAnimationFrame(() => {
      const idx = Math.max(0, nights(start, date) - 2)
      scroller.current?.scrollTo({ left: idx * dayWidth, behavior })
    })
  }, [start, dayWidth])

  const scrollToToday = useCallback((behavior: ScrollBehavior = 'smooth') => {
    scrollToDate(todayISO(), behavior)
  }, [scrollToDate])

  /**
   * Jump to a month. If it falls outside what is rendered, the window is rebuilt
   * around it first — the alternative is growing the DOM by a year to reach March.
   */
  const goToMonth = useCallback((month: string) => {
    setVisibleMonth(month)
    if (month >= start && month < rangeEnd) {
      scrollToDate(month)
      return
    }
    const nextStart = addDays(month, -INITIAL_BACK)
    setStart(nextStart)
    setDayCount(INITIAL_DAYS)
    requestAnimationFrame(() => {
      scroller.current?.scrollTo({ left: (INITIAL_BACK - 2) * dayWidth, behavior: 'instant' })
    })
  }, [start, rangeEnd, dayWidth, scrollToDate])

  /**
   * Growing the window while scrolling.
   *
   * Adding days on the LEFT moves every existing column right, so the scroll offset
   * has to be pushed by exactly the same amount in the same frame — otherwise the
   * calendar jumps backwards under the finger. `pendingShift` carries that amount to
   * the layout effect below, which runs before the browser paints.
   */
  const pendingShift = useRef(0)
  const growing = useRef(false)

  const onScroll = useCallback(() => {
    const el = scroller.current
    if (!el) return

    // Name the month under the left edge, so the header follows the scroll rather
    // than the last button that was pressed.
    const leftDate = addDays(start, Math.floor(el.scrollLeft / dayWidth))
    const m = monthStart(leftDate)
    setVisibleMonth((prev) => (prev === m ? prev : m))

    if (growing.current) return
    const edge = EDGE_DAYS * dayWidth
    if (el.scrollLeft < edge) {
      growing.current = true
      pendingShift.current = GROW_BY * dayWidth
      setStart((s0) => addDays(s0, -GROW_BY))
      setDayCount((c) => c + GROW_BY)
    } else if (el.scrollWidth - el.scrollLeft - el.clientWidth < edge) {
      growing.current = true
      setDayCount((c) => c + GROW_BY)
    }
  }, [start, dayWidth])

  useLayoutEffect(() => {
    if (pendingShift.current && scroller.current) {
      scroller.current.scrollLeft += pendingShift.current
      pendingShift.current = 0
    }
    growing.current = false
  }, [start, dayCount])

  // Open on today rather than on the 1st — nobody wants to scroll to find now.
  useLayoutEffect(() => {
    scrollToToday('instant')
    // Intentionally on mount only; later navigation is driven by the buttons.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="flex flex-col h-full">
      {/*
        Umbrechend statt einzeilig.
        In einer Zeile passten Titel, Monatswahl und vier Knöpfe auf 375 Pixel nicht:
        der Titel wurde auf null Pixel zusammengedrückt — „Belegungskalender" und die
        Auslastung waren auf dem Handy unsichtbar — und der letzte Knopf lag 21 Pixel
        außerhalb des Bildschirms, also außerhalb der Reichweite. Auf schmalen Geräten
        bekommt der Titel deshalb eine eigene Zeile, ab `sm` bleibt alles wie gehabt.
      */}
      <header className="px-4 pt-safe pb-3 sm:py-4 flex flex-wrap items-center
                         gap-x-3 gap-y-2 border-b border-border bg-surface">
        <div className="min-w-0 basis-full sm:basis-auto sm:flex-1">
          <h1 className="font-semibold truncate">{t('calendar.title')}</h1>
          <p className="text-xs text-muted">
            {t('calendar.occupancy')} {occupancy}%
          </p>
        </div>

        {/* Pick a month directly. The arrows still work for stepping, but nobody
            should have to click twelve times to reach next summer. */}
        <select
          value={visibleMonth}
          onChange={(e) => goToMonth(e.target.value)}
          aria-label={t('calendar.month')}
          className="h-9 rounded-xl bg-surface-2 border border-border px-2 text-sm
                     flex-1 min-w-0 sm:flex-none sm:max-w-[10.5rem]"
        >
          {monthChoices.map((m) => (
            <option key={m} value={m}>
              {new Intl.DateTimeFormat(lang === 'de' ? 'de-AT' : 'en-GB',
                { month: 'long', year: 'numeric' }).format(new Date(m + 'T00:00:00'))}
            </option>
          ))}
        </select>

        <Button size="sm" variant="ghost" onClick={() => goToMonth(addMonths(visibleMonth, -1))}
          aria-label="−1">
          <ChevronLeft size={18} />
        </Button>
        <Button size="sm" variant="secondary" onClick={() => scrollToToday()}>{t('nav.today')}</Button>
        <CalendarLegend />
        {/*
          Der Umweg über die Hülle ist nötig, weil `hidden` am Knopf selbst nicht
          greift: die Grundklasse der Schaltfläche setzt `inline-flex`, und welche
          der beiden Anweisungen gewinnt, entscheidet die Reihenfolge im Stylesheet
          und nicht die im Klassenattribut. Der Knopf war deshalb auf dem Handy
          sichtbar — zusätzlich zum schwebenden Knopf unten rechts, der dasselbe tut.
        */}
        <span className="hidden sm:contents">
        <Button size="sm" variant="primary"
          onClick={() => setPicker(true)} aria-label={t('calendar.newBooking')}>
          <Plus size={16} />
        </Button>
        </span>
        <Button size="sm" variant="ghost" onClick={() => goToMonth(addMonths(visibleMonth, 1))}
          aria-label="+1">
          <ChevronRight size={18} />
        </Button>
      </header>

      {/* Die Preisempfehlungen direkt über dem Kalender — dort, wo die Lücke,
          von der sie reden, auch zu sehen ist. */}
      <PriceAdviceBar holidaysByDay={holidays} onJump={(week) => goToMonth(week.slice(0, 7))} />

      <div className="flex-1 min-h-0 overflow-hidden flex">
        {/* frozen unit column */}
        <div className="shrink-0 border-r border-border bg-surface z-10">
          <div className="h-10 border-b border-border" />
          {units.map((u) => (
            <div
              key={u.id}
              style={{ height: rowH }}
              className="w-28 sm:w-40 px-3 flex items-center gap-2 border-b border-border relative group"
            >
              <span className="w-1.5 h-6 rounded-full shrink-0" style={{ background: u.color }} />
              <span className="text-sm truncate">{u.name}</span>
              {/* Drag the divider to set the row height for the whole calendar.
                  Mouse only: on a phone the same pixels are how you scroll. */}
              <div
                onPointerDown={beginRowResize}
                onDoubleClick={() => {
                  setRowH(ROW_H_DEFAULT)
                  try { localStorage.setItem(ROW_H_KEY, String(ROW_H_DEFAULT)) } catch { /* private mode */ }
                }}
                title={t('calendar.rowHeightHint')}
                className="hidden sm:block absolute inset-x-0 -bottom-1 h-2 z-20 cursor-row-resize
                           after:content-[''] after:block after:mx-auto after:mt-[3px]
                           after:w-8 after:h-0.5 after:rounded-full after:bg-transparent
                           hover:after:bg-accent/60"
              />
            </div>
          ))}
        </div>

        {/* scrollable day grid */}
        <div ref={scroller} className="scroll-x flex-1" onScroll={onScroll}>
          <div style={{ width: days.length * dayWidth }}>
            {/* date header */}
            <div className="h-10 flex sticky top-0 bg-surface border-b border-border z-[1]">
              {days.map((d) => {
                const isToday = d === todayISO()
                const hol = holidays.get(d)
                return (
                  <div
                    key={d}
                    style={{ width: dayWidth }}
                    title={holidayTitle(hol, lang === 'de')}
                    className={cx(
                      'shrink-0 text-center border-r border-border/60 pt-1',
                      isWeekend(d) && 'bg-surface-2',
                      isToday && 'bg-accent-soft',
                    )}
                  >
                    <div className={cx('text-[10px] leading-none text-muted', isToday && 'text-accent')}>
                      {new Intl.DateTimeFormat(lang === 'de' ? 'de-AT' : 'en-GB', { weekday: 'short' })
                        .format(new Date(d + 'T00:00:00')).slice(0, 2)}
                    </div>
                    <div className={cx('text-sm leading-tight tabular-nums', isToday && 'font-semibold text-accent')}>
                      {Number(d.slice(8))}
                    </div>
                    {/* Holidays live here, not in lanes of their own: background for a
                        pricing decision, never a row that displaces the units. */}
                    <HolidayHeaderHint day={hol} de={lang === 'de'} />
                  </div>
                )
              })}
            </div>

            {/* unit rows */}
            {units.map((unit, unitIndex) => {
              const rowBookings = bookings.filter((b) => b.unit_id === unit.id)
              const rowEvents = store.db.ical_events.filter(
                (e) => e.unit_id === unit.id && !e.dismissed && !e.booking_id,
              )
              return (
                <div
                  key={unit.id}
                  style={{ height: rowH }}
                  className="relative border-b border-border touch-pan-y"
                  onPointerDown={beginDrag(unit.id)}
                  onPointerMove={moveDrag}
                  onPointerUp={endDrag}
                  onPointerCancel={() => setDrag(null)}
                >
                  {/* day cells */}
                  <div className="absolute inset-0 flex">
                    {days.map((d) => (
                      <div
                        key={d}
                        style={{ width: dayWidth }}
                        className={cx(
                          'shrink-0 border-r border-border/60',
                          isWeekend(d) && 'bg-surface-2/60',
                          d === todayISO() && 'bg-accent-soft/40',
                        )}
                      />
                    ))}
                  </div>

                  {/* portal events sit behind bookings as a thin strip */}
                  {rowEvents.map((e) => (
                    <div
                      key={e.id}
                      title={e.summary}
                      className="absolute bottom-0 h-1.5 rounded-full bg-warn/70"
                      style={{
                        left: (offsetOf(e.arrival) + 0.5) * dayWidth,
                        width: Math.max(0, nights(e.arrival, e.departure)) * dayWidth,
                      }}
                    />
                  ))}

                  {/* bookings */}
                  {rowBookings.map((b) => {
                    const left = (offsetOf(b.arrival) + 0.5) * dayWidth
                    const width = Math.max(0, nights(b.arrival, b.departure)) * dayWidth
                    const isBlock = b.kind === 'block'
                    const beingDragged = ghost?.bookingId === b.id
                    /*
                     * Die Griffe. Ob es sie gibt, entscheidet `gripsFor` — rein und
                     * getestet, weil daran hängt, ob sich eine abgerechnete Buchung
                     * mit dem Finger verschieben lässt. Wie breit sie sein dürfen,
                     * entscheidet `gripWidth`: unter einer Tagesspalte Restmitte gibt
                     * es keine, denn das Verschieben hat auf kurzen Balken Vorrang.
                     */
                    const frozen = lockedBookings.has(b.id)
                    const grips = gripsFor(b, frozen)
                    const gw = gripWidth(width, dayWidth, coarse)
                    const griffe = {
                      start: gw > 0 && grips.start,
                      end: gw > 0 && grips.end,
                    }
                    const label = isBlock
                      ? (b.internal_notes || t('status.block'))
                      : store.guestName(b.guest_id)
                    const signals = signalsOf(b)
                    // Der Tooltip trägt jeden Zustand als Klartext — auch den, für den
                    // auf dem Balken kein Symbol mehr übrig war. Das ist zugleich die
                    // Fassung für Vorlesesoftware, deshalb dieselben Zeilen als
                    // `aria-label`: Farbe und Punkte sagen dort nichts.
                    const titleLines = [
                      b.status === 'option' && b.option_until
                        ? `${t('booking.optionUntil')}: ${formatDate(b.option_until)}`
                        : null,
                      ...(signals ? signalTitleLines(signals, t) : []),
                    ].filter(Boolean) as string[]
                    return (
                      <button
                        key={b.id}
                        data-booking
                        title={titleLines.length ? titleLines.join('\n') : undefined}
                        aria-label={titleLines.length
                          ? [label, ...titleLines].join(' · ') : undefined}
                        onPointerDown={beginMove(b, unitIndex, frozen)}
                        onPointerMove={moveMove}
                        onPointerUp={endMove}
                        onPointerCancel={cancelMoveDrag}
                        className={cx(
                          'absolute top-1.5 h-[calc(100%-0.75rem)] rounded-lg pl-2 pr-1.5 text-left',
                          'text-xs font-medium overflow-hidden shadow-sm flex items-center gap-1',
                          'focus-visible:outline-2 focus-visible:outline-accent',
                          isBlock && 'bg-[repeating-linear-gradient(45deg,var(--color-border),var(--color-border)_6px,transparent_6px,transparent_12px)] text-muted',
                          b.status === 'enquiry' && 'border-2 border-dashed bg-surface',
                          b.status === 'quoted' && 'bg-info-soft text-info',
                          b.status === 'option' && 'bg-warn-soft text-warn',
                          b.status === 'cancelled' && 'opacity-40 line-through bg-surface-2',
                          beingDragged && 'opacity-30',
                          b.status !== 'cancelled' && !frozen && 'cursor-grab active:cursor-grabbing',
                        )}
                        style={{
                          left, width,
                          ...(['confirmed', 'checked_in', 'checked_out'].includes(b.status) && !isBlock
                            ? { background: unit.color, color: '#fff' }
                            : {}),
                        }}
                      >
                        {/*
                          Die Griffe liegen VOLLSTÄNDIG im Balken und sind `<span>`,
                          kein `<button>`: ein zweites fokussierbares Element je Ende
                          verdreifachte die Tabstopps im ganzen Kalender — bei zwanzig
                          Einheiten mit dreißig Buchungen ist das keine Kleinigkeit.
                          Ein überstehender Griff wiederum läge über der Nachbarspalte
                          und nähme dem Aufziehen einer neuen Buchung Pixel weg.
                        */}
                        {griffe.start && (
                          <span
                            data-griff="start"
                            aria-hidden
                            title={t('calendar.gripHint')}
                            style={{ width: gw }}
                            className="absolute inset-y-0 left-0 cursor-col-resize"
                          />
                        )}
                        {griffe.end && (
                          <span
                            data-griff="end"
                            aria-hidden
                            title={t('calendar.gripHint')}
                            style={{ width: gw }}
                            className="absolute inset-y-0 right-0 cursor-col-resize"
                          />
                        )}
                        {/* Ohne den Abzug rutschten Name und Zeichen unter die
                            Griffflächen — lieber ein Zeichen weniger als eines, das
                            man nicht antippen kann. */}
                        <BookingBarContent
                          label={label} signals={signals}
                          width={width - (griffe.start ? gw : 0) - (griffe.end ? gw : 0)}
                          height={rowH - 12}
                        />
                      </button>
                    )
                  })}

                  {/*
                    Preview of where a dragged booking would land.

                    Ein Geist und kein mitwachsender Balken: der echte Datensatz darf
                    während einer noch nicht bestätigten Geste nicht gerendert werden,
                    sonst bliebe er bei einem abgelehnten Schreibversuch verlängert
                    stehen — und der Vermieter verkauft eine Nacht, die er nicht hat.

                    Belegt sieht man ihm an: rot statt grün, und die Beschriftung sagt
                    es. „Erkennbar" ist sonst eine Meinung und kein Prüfergebnis.
                  */}
                  {ghost && ghost.unitId === unit.id && (() => {
                    const belegt = !store.isAvailable(
                      ghost.unitId, ghost.from, ghost.to, ghost.bookingId)
                    // Beschriftet wird das Datum, das sich bewegt — am hinteren Griff
                    // die Abreise, sonst die Anreise —, und die Nächtezahl daneben:
                    // die zwei Zahlen, um die es beim Ziehen geht.
                    const datum = ghost.edge === 'end' ? ghost.to : ghost.from
                    return (
                      <div
                        className={cx(
                          'absolute top-1.5 h-[calc(100%-0.75rem)] rounded-lg border-2',
                          'pointer-events-none flex items-center px-2 text-xs font-medium',
                          belegt
                            ? 'border-danger bg-danger-soft text-danger'
                            : 'border-accent bg-accent-soft/70 text-accent',
                        )}
                        style={{
                          left: (offsetOf(ghost.from) + 0.5) * dayWidth,
                          width: Math.max(1, nights(ghost.from, ghost.to)) * dayWidth,
                        }}
                      >
                        <span className="truncate">
                          {formatDate(datum)} · {nights(ghost.from, ghost.to)}
                          {' '}{nights(ghost.from, ghost.to) === 1
                            ? t('common.night') : t('common.nights')}
                          {belegt && ` · ${t('booking.overlaps')}`}
                        </span>
                      </div>
                    )
                  })()}

                  {/* live drag preview */}
                  {drag?.unitId === unit.id && (
                    <div
                      className="absolute top-1.5 h-[calc(100%-0.75rem)] rounded-lg border-2 border-accent bg-accent-soft/60 pointer-events-none"
                      style={{
                        left: (offsetOf(drag.from) + 0.5) * dayWidth,
                        width: Math.max(1, nights(drag.from, drag.to)) * dayWidth,
                      }}
                    />
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </div>

      <div className="px-4 py-2 border-t border-border bg-surface flex items-center gap-2 text-xs text-muted">
        <Badge tone="ok">{t('status.confirmed')}</Badge>
        <Badge tone="warn">{t('status.option')}</Badge>
        <Badge tone="info">{t('status.quoted')}</Badge>
        {/* Und noch einmal in der Fußzeile, wo die Ferienlegende ohnehin steht: wer
            eine Zeichenerklärung sucht, sucht sie unter dem Kalender, nicht darüber. */}
        <LegendLink />
        <span className="ml-auto flex items-center gap-3">
          <HolidayFootnote coverage={coverage} start={start} regionCount={regionCount} />
          <span className="hidden sm:inline">{t('calendar.dragHint')}</span>
          {/* Erst ab `lg`: auf mittleren Breiten drückt der zweite Satz die
              Ferienlegende aus der Zeile, und die steht länger dort. Wer den Griff
              auf dem Telefon sucht, findet ihn in der Zeichenerklärung. */}
          <span className="hidden lg:inline">{t('calendar.gripHint')}</span>
        </span>
      </div>

      <button
        onClick={() => setPicker(true)}
        className="sm:hidden fixed right-4 bottom-20 z-30 w-14 h-14 rounded-full bg-accent text-accent-fg shadow-lg flex items-center justify-center"
        aria-label={t('calendar.newBooking')}
      >
        <Plus size={24} />
      </button>

      <NewBookingSheet
        open={picker}
        onClose={() => setPicker(false)}
        onPick={(draft, from) => {
          setPicker(false); setPrefill(from); setActive(draft); setSheetOpen(true)
        }}
      />
      <BookingSheet
        booking={active}
        prefill={prefill}
        open={sheetOpen}
        onClose={() => { setSheetOpen(false); setActive(null); setPrefill(undefined) }}
      />
      <BookingChangeDialog change={change} onClose={() => setChange(null)} />
      <span className="sr-only">{formatDate(start)}</span>
    </div>
  )
}
